import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Translate } from 'react-localize-redux';

import styled, { useTheme } from 'styled-components';

import SimpleArtist from '@common/types/artist/simple-artist';
import { NewInstance } from '@common/types/data/new-instance';

import { validateArtist } from '@webapp/utils/validators/artist';

import { Price } from '@common/types/price/price';
import { Product } from '@services/product/types/product';

import AppState from '@webapp/store/state';
import { CREATE_ONBOARDING } from '@webapp/store/onboarding/actions';
import { CreateOnboarding } from '@webapp/store/onboarding/action-creators';
import { productSelector } from '@webapp/store/products/selectors';
import { CreateOrder } from '@webapp/store/orders/action-creators';

import CreateArtistCtrl from '@webapp/controllers/artist/artist-create';

import Hint from '@components/basics/hint';
import Flex from '@components/layout/flex';
import Grid from '@components/basics/grid';
import Typography from '@components/basics/typography';
import { getUserReferrer } from '@webapp/store/auth/selectors';
import { ArtistRelation } from '@common/types/artist/artist-relation';

interface OnboardingArtistProps {
  className?: string;
  isLoading: boolean;
  isOnboarding?: boolean;
  products: Product[];
  referrer?: string;
  selectPlan: (product: Product, price: Price) => void;
  createOnboarding: (
    artist: NewInstance<SimpleArtist>,
    role: ArtistRelation['role']
  ) => CreateOnboarding;
}

const mapStateToProps = (state: AppState) => {
  const products = productSelector(
    state.products.list,
    ['PLAN', 'PLAN_RECORD_LABEL'],
    false
  );
  return {
    products: products.filter(
      (p) => !(['plan_record_label_team'] || []).includes(p.product_key)
    ),
    isLoading: !!state.ui.loading[CREATE_ONBOARDING],
    referrer: getUserReferrer(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createOnboarding: (artistData: SimpleArtist, role: ArtistRelation['role']) =>
    dispatch(new CreateOnboarding(artistData, role)),
  selectPlan: (product: Product, price: Price) =>
    dispatch(
      new CreateOrder({
        product,
        price,
        quantity: 1,
      })
    ),
});

const OnboardingArtist: React.FC<OnboardingArtistProps> = ({
  referrer,
  isOnboarding,
  ...props
}) => {
  const theme = useTheme();

  const [showErrors, setShowErrors] = React.useState<boolean>(false);
  const [artistData, setArtistData] =
    React.useState<NewInstance<SimpleArtist>>();
  const [role, setRole] = React.useState<ArtistRelation['role']>('member');

  const handleNext = () => {
    const isValid = validateArtist(artistData, false);
    setShowErrors(!isValid);
    if (artistData && isValid) {
      props.createOnboarding(artistData, role);
    }
  };

  return (
    <div className={props.className}>
      {isOnboarding ? (
        <Grid className="header" gap={10}>
          <Typography type="subheader" customFont="semi-bold">
            <Translate id="ONBOARDING.ARTIST.TITLE" />
          </Typography>
          <Typography color={theme.colors.tertiary}>
            <Translate id="ONBOARDING.ARTIST.SUBTITLE" />
          </Typography>
        </Grid>
      ) : null}

      <CreateArtistCtrl
        showErrors={showErrors}
        onClick={handleNext}
        role={role}
        isManagedArtist={!!referrer}
        onChange={(data) => setArtistData(data)}
        onChangeRole={setRole}
      />

      <TransparentHint noBorderTop noTitle align="left">
        <Flex flexDirection="column" gap={8}>
          <Typography style={{ fontWeight: 'bold' }}>
            <Translate id="RELEASE.ARTIST_HINT.TEXT1" />
          </Typography>
          <Typography>
            <Translate id="RELEASE.ARTIST_HINT.TEXT2" />
          </Typography>
          <Link style={{ textDecoration: 'underline' }} to={'/catalog'}>
            <Translate id="ONBOARDING.SKIP" />
          </Link>
        </Flex>
      </TransparentHint>
    </div>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(OnboardingArtist)`
  .header {
    margin-bottom: 20px;
  }
`);

const TransparentHint = styled(Hint)`
  padding-left: 20px;
  background-color: ${({ theme }) => theme.colors.inputBackgroundAlternate};
`;
